import { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  Bold, Italic, Underline, Link2, List, ListOrdered, Quote,
} from "lucide-react";
import { cn } from "@/lib/utils";
import {
  initialComposerHtml,
  runComposerCommand,
  sanitizeComposerHtml,
  toggleComposerList,
} from "@/lib/emailComposer";

function ToolbarButton({ onClick, active, title, children, disabled }) {
  return (
    <button
      type="button"
      title={title}
      disabled={disabled}
      onMouseDown={(event) => event.preventDefault()}
      onClick={onClick}
      aria-pressed={active !== undefined ? (active ? "true" : "false") : undefined}
      className={cn(
        "inline-flex h-8 w-8 sm:h-6 sm:w-6 items-center justify-center rounded text-slate-600 shrink-0",
        "hover:bg-slate-100 disabled:opacity-40",
        active && "bg-indigo-50 text-indigo-700",
      )}
    >
      {children}
    </button>
  );
}

ToolbarButton.propTypes = {
  onClick: PropTypes.func.isRequired,
  active: PropTypes.bool,
  title: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  disabled: PropTypes.bool,
};

export function RichTextEditor({
  value = "",
  onChange,
  disabled = false,
  minHeight = 180,
  editorTestId = "rich-text-editor",
  ariaLabel = "Rich text editor",
  resetKey = 0,
}) {
  const editorRef = useRef(null);
  const [activeFormats, setActiveFormats] = useState({
    bold: false,
    italic: false,
    underline: false,
    bullet: false,
    numbered: false,
  });

  const syncToolbarState = useCallback(() => {
    if (typeof document === "undefined") return;
    setActiveFormats({
      bold: document.queryCommandState("bold"),
      italic: document.queryCommandState("italic"),
      underline: document.queryCommandState("underline"),
      bullet: document.queryCommandState("insertUnorderedList"),
      numbered: document.queryCommandState("insertOrderedList"),
    });
  }, []);

  const emitChange = useCallback(() => {
    const el = editorRef.current;
    if (!el || !onChange) return;
    onChange(sanitizeComposerHtml(el.innerHTML));
  }, [onChange]);

  const exec = useCallback((command, val = null) => {
    const el = editorRef.current;
    if (!el) return;
    runComposerCommand(el, command, val);
    syncToolbarState();
    emitChange();
  }, [emitChange, syncToolbarState]);

  const insertLink = useCallback(() => {
    const url = window.prompt("Enter link URL");
    if (url) exec("createLink", url);
  }, [exec]);

  const applyList = useCallback((ordered) => {
    const el = editorRef.current;
    if (!el) return;
    toggleComposerList(el, { ordered });
    syncToolbarState();
    emitChange();
  }, [emitChange, syncToolbarState]);

  useEffect(() => {
    const el = editorRef.current;
    if (!el) return;
    const html = initialComposerHtml({ bodyHtml: value, bodyText: "" }) || "<p><br></p>";
    el.innerHTML = html;
  }, [resetKey, value]);

  return (
    <div className="rounded-md border border-slate-200 overflow-hidden">
      <div
        className="flex items-center gap-0.5 px-1 py-1 border-b border-slate-100 bg-slate-50 shrink-0 overflow-x-auto scrollbar-thin"
        role="group"
        aria-label="Formatting"
      >
        <ToolbarButton title="Bold" disabled={disabled} active={activeFormats.bold} onClick={() => exec("bold")}>
          <Bold size={13} />
        </ToolbarButton>
        <ToolbarButton title="Italic" disabled={disabled} active={activeFormats.italic} onClick={() => exec("italic")}>
          <Italic size={13} />
        </ToolbarButton>
        <ToolbarButton title="Underline" disabled={disabled} active={activeFormats.underline} onClick={() => exec("underline")}>
          <Underline size={13} />
        </ToolbarButton>
        <ToolbarButton title="Link" disabled={disabled} onClick={insertLink}>
          <Link2 size={13} />
        </ToolbarButton>
        <ToolbarButton title="Bullet list" disabled={disabled} active={activeFormats.bullet} onClick={() => applyList(false)}>
          <List size={13} />
        </ToolbarButton>
        <ToolbarButton title="Numbered list" disabled={disabled} active={activeFormats.numbered} onClick={() => applyList(true)}>
          <ListOrdered size={13} />
        </ToolbarButton>
        <ToolbarButton title="Quote" disabled={disabled} onClick={() => exec("formatBlock", "blockquote")}>
          <Quote size={13} />
        </ToolbarButton>
      </div>
      <div
        ref={editorRef}
        contentEditable={!disabled}
        aria-label={ariaLabel}
        aria-multiline="true"
        data-testid={editorTestId}
        onInput={emitChange}
        onKeyUp={syncToolbarState}
        onMouseUp={syncToolbarState}
        onFocus={syncToolbarState}
        style={{ minHeight }}
        className={cn(
          "overflow-y-auto overflow-x-hidden px-3 py-2 text-[13px] leading-relaxed text-slate-800 focus:outline-none",
          "[&_ul]:list-disc [&_ul]:pl-5 [&_ul]:my-1",
          "[&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:my-1",
          "[&_li]:my-0.5",
          "[&_blockquote]:border-l-2 [&_blockquote]:border-slate-300 [&_blockquote]:pl-3",
          "[&_blockquote]:text-slate-600 [&_a]:text-indigo-600 [&_a]:underline",
          disabled && "opacity-60 cursor-not-allowed bg-slate-50",
        )}
      />
    </div>
  );
}

RichTextEditor.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
  minHeight: PropTypes.number,
  editorTestId: PropTypes.string,
  ariaLabel: PropTypes.string,
  resetKey: PropTypes.number,
};
